type GenericResolve<T = any> = {
  (value: T): void;
};

/**
 * a helper class for creating deferred promises
 * @tutorial https://gist.github.com/GFoley83/5877f6c09fbcfd62569c51dc91444cf0
 */
export class DeferredPromise<T> implements Promise<T> {
  [Symbol.toStringTag]: 'Promise';

  private _promise: Promise<T>;
  private _resolve: (value?: T | PromiseLike<T>) => void;
  private _reject: (reason?: any) => void;
  private _state: 'pending' | 'fulfilled' | 'rejected' = 'pending';

  public get state(): 'pending' | 'fulfilled' | 'rejected' {
    return this._state;
  }

  constructor() {
    this._promise = new Promise<T>(
      (resolve: (value: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => {
        this._resolve = resolve;
        this._reject = reject;
      },
    );
  }

  public then<TResult1, TResult2>(
    onfulfilled?: (value: T) => TResult1 | PromiseLike<TResult1>,
    onrejected?: (reason: any) => TResult2 | PromiseLike<TResult2>,
  ): Promise<TResult1 | TResult2> {
    return this._promise.then(onfulfilled, onrejected);
  }

  public catch<TResult>(
    onrejected?: (reason: any) => TResult | PromiseLike<TResult>,
  ): Promise<T | TResult> {
    return this._promise.catch(onrejected);
  }

  public resolve(value?: T | PromiseLike<T>): void {
    this._resolve(value);
    this._state = 'fulfilled';
  }

  public reject(reason?: any): void {
    this._reject(reason);
    this._state = 'rejected';
  }

  public finally(onfinally?: () => void): Promise<T> {
    onfinally?.();
    return this._promise;
  }
}

/**
 * given a type definition of a record and possible values for each key,
 * it generates an array of every permutation
 *
 * @template T
 * @param {{ [k in keyof T]: T[k][] }} input
 * @returns {T[]}
 */
export function generateMatrix<T extends Record<string, any>>(
  input: { [k in keyof T]: T[k][] },
  limit?: number,
): T[] {
  const keys: (keyof T)[] = Object.keys(input);

  // If there's more than 1 key and at least 1 key has a value,
  // then we want to push an empty item to the values that don't have any parameters
  // Otherwise an empty matrix will be generated
  const numValues: number = keys.reduce<number>(
    (sum: number, key: keyof T): number => sum + input[key].length,
    0,
  );
  if (numValues && keys.length) {
    for (const key of keys) {
      if (!input[key].length) {
        // @ts-ignore we want to push an empty item to the values that don't have any parameters
        input[key].push(undefined);
      }
    }
  }

  const params: T[] = Object.entries(input)
    .reduce((partialMatrix: any[], [, values]: [string, any[]]) => {
      if (!partialMatrix.length) {
        return values.map((v) => [v]);
      }

      return partialMatrix.reduce((reduced, current) => {
        values.forEach((val) => {
          const copy = current.slice();
          copy.push(val);
          reduced.push(copy);
        }, []);
        return reduced;
      }, []);
    }, [])
    .reduce((matrix: T[], values: any[]): T[] => {
      const param: T = values.reduce(
        (params: Partial<T>, value: any, index: number): Partial<T> => ({
          ...params,
          [keys[index]]: value,
        }),
        {},
      );
      return [...matrix, param];
    }, []) as unknown as T[];

  return limit ? params.slice(-1 * limit) : params;
}


export function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve: GenericResolve) => setTimeout(resolve, milliseconds));
}